import type { PartialMessage } from '@bufbuild/protobuf';
import { isEqual } from 'lodash-es';

import {
  NoteLineOp,
  NoteSectionOp,
  type DocumentAnswerChange,
  type NoteAddLine,
  type NoteLineChange,
  type NoteSectionChange,
} from '@/shared/api/protocol-ts/api/scribe/svc_encounter_pb';
import {
  DocumentAnswerSource,
  type DocumentAnswers as NoteAnswers,
} from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';
import {
  isStaleAnswersRevisionError,
  type Document,
  type DocumentAnswers,
  type NoteAnswersUpdate,
  type TranscriptorApiClient,
} from '@/shared/api/transcriptor';

import type {
  DocumentSectionSpec,
  DocumentTemplate,
} from '@/entities/templates';

import { isLiveProtocolDocument } from '@/features/audioRecording';

import { EMPTY_BLOCK_PRESET, type NoteBlockPreset } from '../config';

import type { NoteOperation } from './noteOperations';
import {
  indexNoteBlocks,
  isNoteBlockMultiple,
  isNoteOptionBlock,
  normalizeNoteValue,
  type NoteBlockDef,
  type NoteLineDef,
} from './noteView';

// By block id: ids stay the same across versions of the template series.
export type BlockPresets = Record<string, NoteBlockPreset>;

// A line added before the recording. Scribe gives it ids of its own once the
// note exists; until then the client's ids address it.
export type PresetAddedLine = {
  id: string;
  blockId: string;
  sectionId: string;
  text: string;
  // The template or added line it follows; unset is the end of the section.
  afterLineId?: string;
};

// The line operations set before the recording.
export type LinePresets = {
  // Template lines and added lines removed from the note.
  deleted: string[];
  added: PresetAddedLine[];
  // The order a section's lines were dragged into, by section id.
  orders: Record<string, string[]>;
};

export const EMPTY_LINE_PRESETS: LinePresets = {
  deleted: [],
  added: [],
  orders: {},
};

// Everything set on a block note before the recording.
export type NotePresets = { blocks: BlockPresets; lines: LinePresets };

export const hasLinePresets = ({ deleted, added, orders }: LinePresets) =>
  deleted.length > 0 || added.length > 0 || Object.keys(orders).length > 0;

export const hasPresetValue = ({
  values,
  text,
  teeth,
  teethGroups,
  date,
  marks,
  none,
  numbers,
  tableRows,
  removed,
}: NoteBlockPreset) =>
  values.length > 0 ||
  !!text.trim() ||
  teeth.length > 0 ||
  teethGroups.length > 0 ||
  !!date ||
  !!marks?.length ||
  !!none ||
  !!numbers?.length ||
  !!tableRows?.length ||
  !!removed;

const toPreset = (
  {
    values,
    text,
    teeth,
    teethGroups,
    date,
    marks,
    none,
    numbers,
    tableRows,
    removed,
  }: NoteBlockPreset,
  ownOptions: string[],
): NoteBlockPreset => ({
  values,
  text,
  teeth,
  teethGroups,
  ...(ownOptions.length > 0 && { ownOptions }),
  ...(date && { date }),
  ...(marks?.length && { marks }),
  ...(none && { none }),
  ...(numbers?.length && { numbers }),
  ...(tableRows?.length && { tableRows }),
  ...(removed && { removed }),
});

const withoutKey = <T>(record: Record<string, T>, key: string) =>
  Object.fromEntries(Object.entries(record).filter(([item]) => item !== key));

// A block is kept while it holds a value or own options.
const putPreset = (
  presets: BlockPresets,
  blockId: string,
  preset: NoteBlockPreset,
): BlockPresets =>
  hasPresetValue(preset) || preset.ownOptions?.length
    ? { ...presets, [blockId]: preset }
    : withoutKey(presets, blockId);

const isSameNoteValue = (left: string) => (right: string) =>
  normalizeNoteValue(left) === normalizeNoteValue(right);

// "Other" before the start, as scribe applies it: the text becomes an own
// option and the value of a block that takes one, or one more value of a
// block that takes several. The note controls send it for a new text only.
export const addPresetOwnOption = (
  preset: NoteBlockPreset,
  text: string,
  multiple: boolean,
): NoteBlockPreset => {
  const ownOptions = preset.ownOptions ?? [];
  const typed = text.trim();
  const option = ownOptions.find(isSameNoteValue(typed)) ?? typed;
  if (!option) {
    return preset;
  }
  const options = ownOptions.includes(option)
    ? ownOptions
    : [...ownOptions, option];
  if (!multiple) {
    return toPreset({ ...EMPTY_BLOCK_PRESET, text: option }, options);
  }
  const held = preset.values.some(isSameNoteValue(option));
  return toPreset(
    { ...preset, values: held ? preset.values : [...preset.values, option] },
    options,
  );
};

const removePresetOwnOption = (
  preset: NoteBlockPreset,
  text: string,
): NoteBlockPreset => {
  const isOption = isSameNoteValue(text);
  return toPreset(
    {
      ...preset,
      values: preset.values.filter((value) => !isOption(value)),
      text: isOption(preset.text) ? '' : preset.text,
    },
    (preset.ownOptions ?? []).filter((option) => !isOption(option)),
  );
};

// The block controls of the note view report operations in scribe's shape;
// before the start only their block values and own options are kept.
const applyPresetOperation = (
  presets: BlockPresets,
  { update }: NoteOperation,
  template: DocumentTemplate,
): BlockPresets => {
  const blocks = indexNoteBlocks(template, undefined);
  return (update.Changes ?? []).reduce<BlockPresets>((current, change) => {
    const blockId = change.Key?.BlockID;
    if (!blockId) {
      return current;
    }
    const preset = current[blockId] ?? EMPTY_BLOCK_PRESET;
    const def = blocks.get(blockId);
    if (change.AddOwnOption) {
      return putPreset(
        current,
        blockId,
        addPresetOwnOption(
          preset,
          change.Text ?? '',
          !!def && isNoteBlockMultiple(def),
        ),
      );
    }
    if (change.RemoveOwnOption) {
      return putPreset(
        current,
        blockId,
        removePresetOwnOption(preset, change.Text ?? ''),
      );
    }
    const released = change.Clear || change.Release || change.Revert;
    // An empty text on a text block removes its wording, as scribe takes it.
    if (!released && def?.Kind.case === 'Text' && !change.Text?.trim()) {
      return putPreset(current, blockId, {
        ...EMPTY_BLOCK_PRESET,
        removed: true,
      });
    }
    const value = released
      ? EMPTY_BLOCK_PRESET
      : {
          values: change.Values ?? [],
          text: change.Text ?? '',
          teeth: change.Teeth ?? [],
          teethGroups: change.TeethGroups ?? [],
          date: change.Date,
          marks: change.Marks as NoteBlockPreset['marks'],
          none: change.None,
          numbers: change.Numbers as NoteBlockPreset['numbers'],
          tableRows: change.TableRows as NoteBlockPreset['tableRows'],
        };
    return putPreset(
      current,
      blockId,
      toPreset(value, preset.ownOptions ?? []),
    );
  }, presets);
};

const findAddedLineOfBlock = (lines: LinePresets, blockId: string) =>
  lines.added.find((line) => line.blockId === blockId);

const putAddedLine = (
  lines: LinePresets,
  line: PresetAddedLine,
): LinePresets => ({
  ...lines,
  added: lines.added.some((item) => item.id === line.id)
    ? lines.added.map((item) => (item.id === line.id ? line : item))
    : [...lines.added, line],
});

// The wording of an added line is its text: a change rewrites it, and there
// is no template wording to go back to.
const rewriteAddedLine = (
  lines: LinePresets,
  change: PartialMessage<DocumentAnswerChange>,
) => {
  const line = findAddedLineOfBlock(lines, change.Key?.BlockID ?? '');
  const text = change.Text?.trim() ?? '';
  return line && text ? putAddedLine(lines, { ...line, text }) : lines;
};

const applyLineChange = (
  lines: LinePresets,
  { LineID = '', Op }: PartialMessage<NoteLineChange>,
): LinePresets => {
  switch (Op) {
    case NoteLineOp.NoteLineOpDelete:
      return lines.deleted.includes(LineID)
        ? lines
        : { ...lines, deleted: [...lines.deleted, LineID] };
    case NoteLineOp.NoteLineOpRestore:
      return { ...lines, deleted: lines.deleted.filter((id) => id !== LineID) };
    default:
      return lines;
  }
};

const addLine =
  (operationKey: string) =>
  (
    lines: LinePresets,
    {
      SectionID = '',
      AfterLineID = '',
      Text = '',
    }: PartialMessage<NoteAddLine>,
    index: number,
  ) => {
    const text = Text.trim();
    return text
      ? putAddedLine(lines, {
          id: `${operationKey}:line${index}`,
          blockId: `${operationKey}:block${index}`,
          sectionId: SectionID,
          text,
          ...(AfterLineID && { afterLineId: AfterLineID }),
        })
      : lines;
  };

const applySectionChange = (
  lines: LinePresets,
  { SectionID = '', Op, LineIDs = [] }: PartialMessage<NoteSectionChange>,
): LinePresets => {
  switch (Op) {
    case NoteSectionOp.NoteSectionOpSetLineOrder:
      return { ...lines, orders: { ...lines.orders, [SectionID]: LineIDs } };
    case NoteSectionOp.NoteSectionOpResetLineOrder:
      return { ...lines, orders: withoutKey(lines.orders, SectionID) };
    default:
      return lines;
  }
};

// The note controls report scribe's operations; before the start they are
// kept as presets. "Skip remaining lines" and line notes need the note.
export const applyNotePresetOperation = (
  presets: NotePresets,
  operation: NoteOperation,
  template: DocumentTemplate,
): NotePresets => {
  const { update } = operation;
  const changes = update.Changes ?? [];
  const onAddedLine = (change: PartialMessage<DocumentAnswerChange>) =>
    !!findAddedLineOfBlock(presets.lines, change.Key?.BlockID ?? '');
  const rewritten = changes
    .filter(onAddedLine)
    .reduce(rewriteAddedLine, presets.lines);
  const changedLines = (update.LineChanges ?? []).reduce(
    applyLineChange,
    rewritten,
  );
  const added = (update.AddLines ?? []).reduce(
    addLine(operation.key),
    changedLines,
  );
  return {
    blocks: applyPresetOperation(
      presets.blocks,
      {
        ...operation,
        update: {
          Changes: changes.filter((change) => !onAddedLine(change)),
        },
      },
      template,
    ),
    lines: (update.SectionChanges ?? []).reduce(applySectionChange, added),
  };
};

// The lines the chosen values show, by the rule scribe applies (a trigger on
// a shown line that is not deleted holds one of the condition's values,
// ignoring case and spacing), in the order they are shown when the values of
// one line after another are set. Scribe checks each value of an update
// against the note the earlier values left, so a trigger's line comes before
// the lines it shows even when the template places it below them; otherwise
// the template order holds.
const orderPresetLines = (
  template: DocumentTemplate,
  presets: BlockPresets,
  deleted: string[],
): NoteLineDef[] => {
  const lines = template.sections.flatMap((section) => section.Lines);
  const lineOfBlock = new Map(
    lines.flatMap((line) =>
      line.Blocks.map((block): [string, string] => [block.ID, line.ID]),
    ),
  );
  const takeNextShown = (taken: NoteLineDef[]) => {
    const takenIds = new Set(taken.map((line) => line.ID));
    const next = lines.find(
      (line) =>
        !takenIds.has(line.ID) &&
        (!line.ShowWhen.length ||
          line.ShowWhen.some(({ BlockID, Values }) => {
            const triggerLineId = lineOfBlock.get(BlockID) ?? '';
            return (
              takenIds.has(triggerLineId) &&
              !deleted.includes(triggerLineId) &&
              (presets[BlockID]?.values ?? []).some((value) =>
                Values.some(
                  (expected) =>
                    normalizeNoteValue(expected) === normalizeNoteValue(value),
                ),
              )
            );
          })),
    );
    return next ? [...taken, next] : taken;
  };
  return lines.reduce<NoteLineDef[]>((taken) => takeNextShown(taken), []);
};

// A display hint only: the lines the chosen values show, in template order.
export const getPresetShownLineIds = (
  template: DocumentTemplate,
  presets: BlockPresets,
  deleted: string[] = [],
): string[] => {
  const shown = new Set(
    orderPresetLines(template, presets, deleted).map((line) => line.ID),
  );
  return template.sections
    .flatMap((section) => section.Lines)
    .filter((line) => shown.has(line.ID))
    .map((line) => line.ID);
};

// The values sent to the new encounter's note: only on lines its values show
// that are not deleted, in an order scribe accepts. Scribe refuses the whole
// update when one value sits on a hidden line. The key order carries the send
// order: block ids are never integer-like.
const retainBlockPresets = (
  template: DocumentTemplate,
  presets: BlockPresets | undefined,
  deleted: string[] = [],
): BlockPresets => {
  const chosen = presets ?? {};
  return Object.fromEntries(
    orderPresetLines(template, chosen, deleted)
      .filter((line) => !deleted.includes(line.ID))
      .flatMap((line) =>
        line.Blocks.flatMap((block) => {
          const preset = chosen[block.ID];
          return preset ? [[block.ID, preset] as const] : [];
        }),
      ),
  );
};

// The values of blocks, the added lines and the orders an operation changes,
// before or after it: null where there is none, and whether a line is
// deleted.
export type NotePresetsPatch = {
  blocks: Record<string, NoteBlockPreset | null>;
  deleted: Record<string, boolean>;
  added: Record<string, PresetAddedLine | null>;
  orders: Record<string, string[] | null>;
};

const changedKeys = <T>(before: Record<string, T>, after: Record<string, T>) =>
  [...new Set([...Object.keys(before), ...Object.keys(after)])].filter(
    (key) => !isEqual(before[key], after[key]),
  );

const pickKeys = <T>(
  record: Record<string, T>,
  keys: string[],
): Record<string, T | null> =>
  Object.fromEntries(keys.map((key) => [key, record[key] ?? null]));

const indexAddedLines = (lines: PresetAddedLine[]) =>
  Object.fromEntries(lines.map((line) => [line.id, line]));

const flagIds = (ids: string[]) =>
  Object.fromEntries(ids.map((id) => [id, true]));

// What an operation changed, to undo or redo it alone: other sections may
// have changed the presets since. Null when nothing changed.
export const diffNotePresets = (
  before: NotePresets,
  after: NotePresets,
): { before: NotePresetsPatch; after: NotePresetsPatch } | null => {
  const blocks = changedKeys(before.blocks, after.blocks);
  const deleted = changedKeys(
    flagIds(before.lines.deleted),
    flagIds(after.lines.deleted),
  );
  const added = changedKeys(
    indexAddedLines(before.lines.added),
    indexAddedLines(after.lines.added),
  );
  const orders = changedKeys(before.lines.orders, after.lines.orders);
  if (![blocks, deleted, added, orders].some((keys) => keys.length)) {
    return null;
  }
  const patchOf = (presets: NotePresets): NotePresetsPatch => ({
    blocks: pickKeys(presets.blocks, blocks),
    deleted: Object.fromEntries(
      deleted.map((id) => [id, presets.lines.deleted.includes(id)]),
    ),
    added: pickKeys(indexAddedLines(presets.lines.added), added),
    orders: pickKeys(presets.lines.orders, orders),
  });
  return { before: patchOf(before), after: patchOf(after) };
};

type NotePresetsChange = { before: NotePresetsPatch; after: NotePresetsPatch };

const mergePatches = (
  base: NotePresetsPatch,
  over: NotePresetsPatch,
): NotePresetsPatch => ({
  blocks: { ...base.blocks, ...over.blocks },
  deleted: { ...base.deleted, ...over.deleted },
  added: { ...base.added, ...over.added },
  orders: { ...base.orders, ...over.orders },
});

// Two changes made one after the other as one: what was before the first,
// and what is after the second.
export const mergeNotePresetsChanges = (
  first: NotePresetsChange,
  second: NotePresetsChange,
): NotePresetsChange => ({
  before: mergePatches(second.before, first.before),
  after: mergePatches(first.after, second.after),
});

export const patchNotePresets = (
  presets: NotePresets,
  patch: NotePresetsPatch,
): NotePresets => ({
  blocks: Object.entries(patch.blocks).reduce<BlockPresets>(
    (current, [blockId, preset]) =>
      preset ? { ...current, [blockId]: preset } : withoutKey(current, blockId),
    presets.blocks,
  ),
  lines: {
    deleted: [
      ...presets.lines.deleted.filter((id) => patch.deleted[id] !== false),
      ...Object.keys(patch.deleted).filter(
        (id) => patch.deleted[id] && !presets.lines.deleted.includes(id),
      ),
    ],
    added: Object.entries(patch.added).reduce<LinePresets>(
      (lines, [id, line]) =>
        line
          ? putAddedLine(lines, line)
          : {
              ...lines,
              added: lines.added.filter((item) => item.id !== id),
            },
      presets.lines,
    ).added,
    orders: Object.entries(patch.orders).reduce<Record<string, string[]>>(
      (orders, [sectionId, order]) =>
        order
          ? { ...orders, [sectionId]: order }
          : withoutKey(orders, sectionId),
      presets.lines.orders,
    ),
  },
});

// A section's lines in the order scribe shows them (`orderNoteLines`): the
// template order with each added line after the line it follows and the
// others at the end, or the dragged order, where a line it does not list
// follows the line before it in that default order.
export const orderPresetSectionLines = (
  section: Pick<DocumentSectionSpec, 'ID' | 'Lines'>,
  lines: LinePresets,
): string[] => {
  const added = lines.added.filter((line) => line.sectionId === section.ID);
  const known = new Set([
    ...section.Lines.map((line) => line.ID),
    ...added.map((line) => line.id),
  ]);
  const isAnchored = ({ id, afterLineId }: PresetAddedLine) =>
    !!afterLineId && afterLineId !== id && known.has(afterLineId);
  const placeWithFollowers = (lineId: string, placed: string[]): string[] =>
    placed.includes(lineId)
      ? placed
      : added
          .filter((line) => isAnchored(line) && line.afterLineId === lineId)
          .reduce(
            (order, line) => placeWithFollowers(line.id, order),
            [...placed, lineId],
          );
  // Lines whose anchors only lead to each other go last.
  const defaultOrder = [
    ...section.Lines.map((line) => line.ID),
    ...added.filter((line) => !isAnchored(line)).map((line) => line.id),
    ...added.map((line) => line.id),
  ].reduce<string[]>((order, lineId) => placeWithFollowers(lineId, order), []);
  const listed = [
    ...new Set(
      (lines.orders[section.ID] ?? []).filter((id) =>
        defaultOrder.includes(id),
      ),
    ),
  ];
  return defaultOrder.reduce<string[]>((order, lineId, position) => {
    if (order.includes(lineId)) {
      return order;
    }
    const at = position ? order.indexOf(defaultOrder[position - 1]) + 1 : 0;
    return [...order.slice(0, at), lineId, ...order.slice(at)];
  }, listed);
};

// A block's own options go first, each chosen as it is added; its value
// follows, or a release when it holds none.
const toPresetChanges = (
  presets: BlockPresets,
): PartialMessage<DocumentAnswerChange>[] =>
  Object.entries(presets).flatMap(([BlockID, preset]) => [
    ...(preset.ownOptions ?? []).map((Text) => ({
      Key: { BlockID },
      Text,
      AddOwnOption: true,
    })),
    hasPresetValue(preset)
      ? {
          Key: { BlockID },
          Values: preset.values,
          Text: preset.text,
          Teeth: preset.teeth,
          TeethGroups: preset.teethGroups,
          ...(preset.date && { Date: preset.date }),
          ...(preset.marks?.length && { Marks: preset.marks }),
          ...(preset.none && { None: true }),
          ...(preset.numbers?.length && { Numbers: preset.numbers }),
          ...(preset.tableRows?.length && { TableRows: preset.tableRows }),
        }
      : { Key: { BlockID }, Release: true },
  ]);

// What the new encounter's note receives. Scribe applies the lists of one
// update in its order (Changes, LineChanges, AddLines, SectionChanges) and
// mints the ids of the lines it adds, so the update places an added line
// after the template line before it and orders the template lines only. An
// added line above every template line of its section has no such anchor:
// that section's whole order follows in a second update, once the note
// names the added lines.
export type PresetNoteRequest = {
  update: NoteAnswersUpdate;
  // The client's ids of the added lines, in the order of `update.AddLines`.
  addedLineIds: string[];
  laterOrders: { sectionId: string; lineIds: string[] }[];
};

export const buildPresetNoteRequest = (
  template: DocumentTemplate,
  { blocks, lines }: NotePresets,
): PresetNoteRequest => {
  const templateLineIds = template.sections.flatMap((section) =>
    section.Lines.map((line) => line.ID),
  );
  const sections = template.sections.map((section) => {
    const templateIds = section.Lines.map((line) => line.ID);
    const isTemplateLine = (id: string) => templateIds.includes(id);
    const sent = lines.added.filter(
      (line) =>
        line.sectionId === section.ID && !lines.deleted.includes(line.id),
    );
    const order = orderPresetSectionLines(section, lines).filter(
      (id) => isTemplateLine(id) || sent.some((line) => line.id === id),
    );
    const added = order.flatMap((id, position) => {
      const line = sent.find((item) => item.id === id);
      const afterLineId =
        order.slice(0, position).filter(isTemplateLine).at(-1) ?? '';
      return line ? [{ line, afterLineId }] : [];
    });
    const templateOrder = order.filter(isTemplateLine);
    return {
      section,
      order,
      added,
      templateOrder,
      reordered: !isEqual(templateOrder, templateIds),
      leading:
        templateIds.length > 0 && added.some(({ afterLineId }) => !afterLineId),
    };
  });
  const changes = toPresetChanges(
    retainBlockPresets(template, blocks, lines.deleted),
  );
  const lineChanges = lines.deleted
    .filter((id) => templateLineIds.includes(id))
    .map((LineID) => ({ LineID, Op: NoteLineOp.NoteLineOpDelete }));
  const addLines = sections.flatMap(({ section, added }) =>
    added.map(({ line, afterLineId }) => ({
      SectionID: section.ID,
      AfterLineID: afterLineId,
      Text: line.text,
    })),
  );
  const sectionChanges = sections
    .filter(({ reordered }) => reordered)
    .map(({ section, templateOrder }) => ({
      SectionID: section.ID,
      Op: NoteSectionOp.NoteSectionOpSetLineOrder,
      LineIDs: templateOrder,
    }));
  return {
    update: {
      ...(changes.length > 0 && { Changes: changes }),
      ...(lineChanges.length > 0 && { LineChanges: lineChanges }),
      ...(addLines.length > 0 && { AddLines: addLines }),
      ...(sectionChanges.length > 0 && { SectionChanges: sectionChanges }),
    },
    addedLineIds: sections.flatMap(({ added }) =>
      added.map(({ line }) => line.id),
    ),
    laterOrders: sections
      .filter(({ leading }) => leading)
      .map(({ section, order }) => ({
        sectionId: section.ID,
        lineIds: order,
      })),
  };
};

export const isPresetNoteRequestEmpty = ({ update }: PresetNoteRequest) =>
  !Object.keys(update).length;

const keepIds = (ids: string[], known: Set<string>) =>
  ids.filter((id) => known.has(id));

// The presets a template version can take: the encounter may pin a newer
// version than the working copy's, and scribe refuses the whole update for
// one block, line or section it does not know. Ids stay the same across the
// versions of a series, so the rest carries over.
export const retainNotePresets = (
  template: DocumentTemplate,
  { blocks, lines }: NotePresets,
): NotePresets => {
  const sectionIds = new Set(template.sections.map((section) => section.ID));
  const templateLines = template.sections.flatMap((section) => section.Lines);
  const blocksById = new Map(
    templateLines.flatMap((line) =>
      line.Blocks.map((block): [string, NoteBlockDef] => [block.ID, block]),
    ),
  );
  const added = lines.added.filter((line) => sectionIds.has(line.sectionId));
  const lineIds = new Set([
    ...templateLines.map((line) => line.ID),
    ...added.map((line) => line.id),
  ]);
  return {
    blocks: Object.entries(blocks).reduce<BlockPresets>(
      (kept, [blockId, preset]) => {
        const block = blocksById.get(blockId);
        if (!block) {
          return kept;
        }
        // Scribe refuses the whole update for an own option on a block that
        // takes none.
        return isNoteOptionBlock(block)
          ? { ...kept, [blockId]: preset }
          : putPreset(kept, blockId, toPreset(preset, []));
      },
      {},
    ),
    lines: {
      deleted: keepIds(lines.deleted, lineIds),
      added,
      orders: Object.fromEntries(
        Object.entries(lines.orders)
          .filter(([sectionId]) => sectionIds.has(sectionId))
          .map(([sectionId, order]) => [sectionId, keepIds(order, lineIds)]),
      ),
    },
  };
};

type NoteUpdateTarget = {
  api: Pick<
    TranscriptorApiClient,
    'getDocumentAnswers' | 'updateDocumentAnswers'
  >;
  encounterId: string;
  documentId: string;
};

// A live fill may store answers first; the update is then applied once on
// top of the stored revision.
const storeNoteUpdate = (
  { api, encounterId, documentId }: NoteUpdateTarget,
  update: NoteAnswersUpdate,
  baseRevision: number,
) => {
  const submit = (revision: number) =>
    api.updateDocumentAnswers({
      encounterId,
      documentId,
      baseRevision: revision,
      changes: [],
      noteUpdates: [update],
    });
  return submit(baseRevision).catch(async (error: unknown) => {
    if (!isStaleAnswersRevisionError(error)) {
      throw error;
    }
    const current = await api.getDocumentAnswers({ encounterId, documentId });
    return submit(current?.revision ?? 0);
  });
};

export type PushedBlockPresets = {
  documentId: string;
  answers: DocumentAnswers | null;
  // The request built for the version the encounter pinned.
  request: PresetNoteRequest;
  // Every preset reached the note: none was on what that version lacks.
  complete: boolean;
};

// The block note of the new encounter receives the presets in one update.
export const pushBlockPresets = async ({
  api,
  encounterId,
  documents,
  template,
  presets,
  loadTemplate,
}: Omit<NoteUpdateTarget, 'documentId'> & {
  documents: Document[];
  // The working copy's version.
  template: DocumentTemplate;
  presets: NotePresets;
  loadTemplate: (templateId: string) => Promise<DocumentTemplate>;
}): Promise<PushedBlockPresets> => {
  const document = documents.find(
    (item) =>
      isLiveProtocolDocument(item) &&
      item.template_series_id === template.series_id,
  );
  if (!document?.id) {
    throw new Error(
      `No protocol document for template series ${template.series_id}`,
    );
  }
  const documentId = document.id;
  const pinned =
    document.template_id && document.template_id !== template.id
      ? await loadTemplate(document.template_id)
      : template;
  const retained = retainNotePresets(pinned, presets);
  const request = buildPresetNoteRequest(pinned, retained);
  const complete = isEqual(retained, presets);
  if (isPresetNoteRequestEmpty(request)) {
    return { documentId, answers: null, request, complete };
  }
  const answers = await storeNoteUpdate(
    { api, encounterId, documentId },
    request.update,
    0,
  );
  return { documentId, answers, request, complete };
};

// Scribe appends the lines an update adds after every line added before, in
// the update's order.
const toMintedOrderUpdate = (
  { addedLineIds, laterOrders }: PresetNoteRequest,
  note: NoteAnswers | undefined,
): NoteAnswersUpdate => {
  const manual = (note?.AddedLines ?? []).filter(
    (added) => added.Source === DocumentAnswerSource.DocumentAnswerSourceManual,
  );
  const minted = manual.slice(manual.length - addedLineIds.length);
  if (minted.length !== addedLineIds.length) {
    throw new Error('The note does not hold the lines added before the start');
  }
  const ids = new Map(
    addedLineIds.map((id, index) => [id, minted[index].Line?.ID ?? id]),
  );
  return {
    SectionChanges: laterOrders.map(({ sectionId, lineIds }) => ({
      SectionID: sectionId,
      Op: NoteSectionOp.NoteSectionOpSetLineOrder,
      LineIDs: lineIds.map((id) => ids.get(id) ?? id),
    })),
  };
};

// The second update a request needs when an added line was dragged above
// every template line of its section, sent once the first one is stored.
export const pushPresetLineOrders = ({
  request,
  stored,
  ...target
}: NoteUpdateTarget & {
  request: PresetNoteRequest;
  stored: DocumentAnswers | null;
}): Promise<DocumentAnswers | null> =>
  Promise.resolve()
    .then(() => toMintedOrderUpdate(request, stored?.note))
    .then((update) => storeNoteUpdate(target, update, stored?.revision ?? 0));
